import React from 'react';
import DI from './utility/DI';
import MCPOAuthSection from './utility/MCPOAuthSection';
import { 
  Home, Users, BarChart3, Tag, Save, Building2, Info, 
  TrendingUp, UserPlus, Activity, MapPin, LayoutDashboard,
  Clock, PlusCircle, Wallet, Sparkles, Target
} from 'lucide-react';

const About = (props) => {
    const {
    di: { session:userData },
  } = props;

  const latestUpdates = [
    "Bar chart now displays recent months first with proper pagination",
    "Implemented pie chart to visualize month-wise expense categorization",
    "Only title and price are mandatory fields in expense form",
    "Persistent category selection for faster data entry",
    "Users can now delete their home along with all associated data, if needed",
    "Comprehensive About section with module explanations"
  ];

  const modules = [
    {
      icon: <Home className="w-8 h-8" />,
      title: "Home Expenses",
      route: "/home",
      description: "Track household expenses with detailed categorization. Manage expenses for multiple homes with specific categories, brands, weights, and quantities.",
      gradient: "from-blue-500 to-blue-600"
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "Room Expenses",
      route: "/panel",
      description: "Manage shared expenses with roommates. Split costs equally or unequally, track who owes what, and manage collaborative transactions.",
      gradient: "from-purple-500 to-purple-600"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Friends & Connections",
      route: "/panel/friends",
      description: "Add and manage friends for collaborative expense sharing. Connect with others to track shared expenses and settle balances.",
      gradient: "from-orange-500 to-orange-600"
    },
    {
      icon: <LayoutDashboard className="w-8 h-8" />,
      title: "Dashboard & Analytics",
      route: "/panel",
      description: "Visualize spending patterns with interactive charts and graphs. Track transaction history and gain insights into financial habits.",
      gradient: "from-green-500 to-green-600"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Pending Transactions",
      route: "/panel/transactions",
      description: "View and manage pending expense approvals. Track who needs to confirm shared expenses and manage the approval workflow.",
      gradient: "from-red-500 to-red-600"
    },
    {
      icon: <PlusCircle className="w-8 h-8" />,
      title: "Home Management",
      route: "/home/add",
      description: "Create and manage multiple homes or properties. Add household members and configure expense tracking for each location.",
      gradient: "from-cyan-500 to-cyan-600"
    }
  ];

  const features = [
    {
      icon: <Tag className="w-6 h-6" />,
      title: "Expense Categorization",
      description: "Organize expenses into 12 predefined categories including housing, groceries, transportation, health, and education."
    },
    {
      icon: <UserPlus className="w-6 h-6" />,
      title: "Collaborative Sharing",
      description: "Easily split expenses among family members or friends with automatic calculation of individual shares."
    },
    {
      icon: <Activity className="w-6 h-6" />,
      title: "Analytics & Reports",
      description: "Visualize spending patterns with interactive charts and detailed reports to understand financial habits."
    },
    {
      icon: <Building2 className="w-6 h-6" />,
      title: "Multi-House Support",
      description: "Manage expenses for multiple homes or properties from a single interface."
    },
    {
      icon: <Save className="w-6 h-6" />,
      title: "Persistent Selections",
      description: "Last selected categories are remembered for faster data entry when adding multiple expenses."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Advanced Analytics",
      description: "Interactive charts and detailed reports to visualize spending patterns and financial habits."
    }
  ];

  return (
    <div className="min-h-screen from-slate-50 to-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

          {userData && <UserProfileCard userData={userData} />}

        {/* Hero Section */}
        {/* <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 sm:p-12 mb-8 overflow-hidden shadow-2xl">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white opacity-5 rounded-full -ml-48 -mb-48"></div>
           */}
          {/* <div className="relative z-10 text-center text-white"> */}
            {/* <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-6">
              <Wallet className="w-10 h-10" />
            </div> */}
            {/* <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4">
              About Spent App
            </h1> */}
            {/* <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto">
              Your comprehensive expense tracking solution for households and groups
            </p> */}
          {/* </div> */}
        {/* </div> */}

        {/* Latest Updates */}
        <div className="bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl p-6 sm:p-8 mb-8 shadow-xl">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-8 h-8 text-white" />
            <h2 className="text-2xl sm:text-3xl font-bold text-white">Latest Updates</h2>
          </div>
          <ul className="space-y-2">
            {latestUpdates.map((update, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="inline-block px-3 py-1 bg-white/25 backdrop-blur-sm text-white text-xs font-bold rounded-full mt-0.5">
                  NEW
                </span>
                <p className="text-white/95 flex-1">{update}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* MCP OAuth Section */}
        {userData && <MCPOAuthSection userId={userData.id} />}

        {/* What is Spent App */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 mb-8 shadow-lg border border-blue-100">
          <div className="flex items-center gap-3 mb-4">
            <Info className="w-8 h-8 text-blue-600" />
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">What is Spent App?</h2>
          </div>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p className="text-lg">
              Spent App is a comprehensive expense tracking application designed to help individuals, families, and groups manage their finances efficiently. Our platform simplifies the process of recording, categorizing, and analyzing expenses, making financial management accessible to everyone.
            </p>
            <p className="text-lg">
              Whether you're splitting bills with roommates, managing household expenses, or tracking personal spending, Spent App provides the tools you need to stay organized and financially aware.
            </p>
          </div>
        </div>

        {/* Application Modules */}
        <div className="mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-8 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Application Modules
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-100"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${module.gradient} rounded-2xl mb-4 text-white shadow-lg`}>
                  {module.icon}
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">{module.title}</h3>
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded-full mb-3">
                  {module.route}
                </span>
                <p className="text-gray-600 leading-relaxed">{module.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Key Features */}
        <div className="mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-8 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Key Features
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-300"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl mb-4 text-white">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Mission */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-green-100">
          <div className="flex items-center gap-3 mb-4">
            <Target className="w-8 h-8 text-green-600" />
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Our Mission</h2>
          </div>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p className="text-lg">
              At Spent App, we believe that financial management should be simple, intuitive, and accessible to everyone. Our mission is to empower individuals and families to take control of their finances by providing powerful yet easy-to-use tools for expense tracking and analysis.
            </p>
            <p className="text-lg">
              We continuously strive to improve our platform based on user feedback and emerging financial trends, ensuring that our users have access to the most effective tools for managing their money.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <p className="text-gray-500">
            © {new Date().getFullYear()} Spent App. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DI(About);

const UserProfileCard = ({ userData }) => {

  const user = userData;

  const formatDate = (dateString) => {
   const formattedDateString = dateString.replace(' ', 'T');
    const date = new Date(formattedDateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
   <div className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 rounded-xl p-4 sm:p-6 shadow-2xl overflow-hidden mb-8">
      {/* Abstract geometric patterns */}
      <div className="absolute top-0 right-0 w-64 h-64 opacity-10">
        <div className="absolute top-0 right-0 w-32 h-32 border-4 border-gray-600 rounded-full"></div>
        <div className="absolute top-8 right-8 w-24 h-24 border-2 border-gray-500"></div>
        <div className="absolute top-16 right-16 w-16 h-16 bg-gray-700 rounded-full"></div>
      </div>
      
      <div className="absolute bottom-0 left-0 w-72 h-72 opacity-5">
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-gray-600 to-transparent rounded-full"></div>
        <div className="absolute bottom-10 left-10 w-32 h-32 border-4 border-gray-600 rotate-45"></div>
        <div className="absolute bottom-20 left-20 w-24 h-1 bg-gray-600"></div>
        <div className="absolute bottom-24 left-16 w-1 h-24 bg-gray-600"></div>
      </div>

      {/* Diagonal line accent */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
        <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-gray-500 via-gray-600 to-transparent transform rotate-12 origin-top-right"></div>
      </div>

      <div className="relative z-10">
        {/* Row 1: Avatar, Name, Email */}
        <div className="flex items-center gap-3 mb-3">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-900 rounded-lg flex items-center justify-center border-2 border-gray-600 shadow-lg">
              <span className="text-white text-lg font-bold">
                {user.firstname.charAt(0).toUpperCase()}{user.lastname.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-black"></div>
          </div>

          {/* Name and Email */}
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-lg font-bold text-white capitalize tracking-wide truncate">
              {user.firstname} {user.lastname}
            </h2>
            <p className="text-gray-400 text-xs truncate">{user.email}</p>
          </div>

        </div>

        {/* Row 2: ID and Date */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-800 gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-gray-500 text-xs">🆔</span>
            <span className="text-gray-400 text-xs">
              ID: <span className="text-white font-mono">{user.id}</span>
            </span>
          </div>

          <div className="flex items-center gap-2 min-w-0">
            <span className="text-gray-400 text-xs whitespace-nowrap">
                  {formatDate(user.created_at)}
            </span>
          </div>
        </div>

        {/* Corner accent */}
        <div className="absolute bottom-4 right-4 opacity-20">
          <div className="flex gap-1">
            <div className="w-1 h-8 bg-gray-600"></div>
            <div className="w-1 h-6 bg-gray-600"></div>
            <div className="w-1 h-4 bg-gray-600"></div>
          </div>
        </div>
      </div>
    </div>
  );
};


